import {
  ExchangeFromPositionMatchRes,
  PositionMatch,
} from '../model/position-match.model'

export const unitsToleranceForExchange = 0.0001

/**
 * Determines whether an exchange can be made between two position matches and returns the exchange parameters.
 */
export function getExchangeFromPositionMatch(
  firstPositionMatch: PositionMatch,
  secondPositionMatch: PositionMatch,
): ExchangeFromPositionMatchRes {
  if (
    firstPositionMatch.matchStatus != 'EXTERNAL_ONLY' &&
    !secondPositionMatch.securityId
  ) {
    return {
      canExchange: false,
      outCusip: '',
      inCusip: '',
      rate: 0,
      units: 0,
    }
  }
  if (
    secondPositionMatch.matchStatus != 'EXTERNAL_ONLY' &&
    !secondPositionMatch.securityId
  ) {
    return {
      canExchange: false,
      outCusip: '',
      inCusip: '',
      rate: 0,
      units: 0,
    }
  }

  if (canMakeExchange(firstPositionMatch, secondPositionMatch))
    return extractExchangeParamsFromPositionMatch(
      firstPositionMatch,
      secondPositionMatch,
    )

  return {
    canExchange: false,
    outCusip: '',
    inCusip: '',
    rate: 0,
    units: 0,
  }
}

/**
 * Determines if an exchange can be made between two position matches based on their statuses.
 */
export function canMakeExchange(
  firstPositionMatch: PositionMatch,
  secondPositionMatch: PositionMatch,
): boolean {
  const firstExternal = firstPositionMatch.matchStatus == 'EXTERNAL_ONLY'
  const secondExternal = secondPositionMatch.matchStatus == 'EXTERNAL_ONLY'

  return (
    (firstExternal && secondPositionMatch.matchStatus == 'CW_ONLY') ||
    (secondExternal && firstPositionMatch.matchStatus == 'CW_ONLY') ||
    (firstExternal &&
      secondPositionMatch.matchStatus == 'MATCHED' &&
      areUnitsApproxEqual(secondPositionMatch.externalUnits, 0.0)) ||
    (secondExternal &&
      firstPositionMatch.matchStatus == 'MATCHED' &&
      areUnitsApproxEqual(firstPositionMatch.externalUnits, 0.0))
  )
}

/**
 * Extracts the exchange parameters from two position matches if an exchange can be made.
 */
export function extractExchangeParamsFromPositionMatch(
  firstPositionMatch: PositionMatch,
  secondPositionMatch: PositionMatch,
): ExchangeFromPositionMatchRes {
  const externalFirst = firstPositionMatch.matchStatus == 'EXTERNAL_ONLY'

  const externalPosUnits = externalFirst
    ? firstPositionMatch.externalUnits
    : secondPositionMatch.externalUnits

  const cwanPosUnits = externalFirst
    ? secondPositionMatch.cwanUnits
    : firstPositionMatch.cwanUnits

  const isOneForOne = areUnitsApproxEqual(externalPosUnits, cwanPosUnits)

  const exchangeRate = isOneForOne ? 1.0 : 0.0

  const outCusip = externalFirst
    ? secondPositionMatch.cusip
    : firstPositionMatch.cusip

  const inCusip = externalFirst
    ? firstPositionMatch.cusip
    : secondPositionMatch.cusip

  const outCwanIdentifier = externalFirst
    ? secondPositionMatch.cwanIdentifier
    : firstPositionMatch.cwanIdentifier

  const inCwanIdentifier = externalFirst
    ? firstPositionMatch.cwanIdentifier
    : secondPositionMatch.cwanIdentifier

  return {
    canExchange: true,
    outCusip: outCusip,
    inCusip: inCusip,
    outCwanIdentifier: outCwanIdentifier,
    inCwanIdentifier: inCwanIdentifier,
    rate: exchangeRate,
    units: cwanPosUnits,
  }
}

/**
 * Compares two unit values to determine if they are approximately equal within a defined tolerance.
 */
export function areUnitsApproxEqual(
  externalPosUnits: number,
  cwanPosUnits: number,
): boolean {
  return Math.abs(externalPosUnits - cwanPosUnits) <= unitsToleranceForExchange
}
